import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Logo } from '../components/Logo'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { useOnboardingStore } from '../store/onboardingStore'
import { useTenant } from '../context/TenantContext'
import { submissionService } from '../services/api'
import { logFlowEvent } from '../services/flowEventService'
import { Lock } from 'lucide-react'
import { useIsItaly } from './italy/useIsItaly'
import type { StepProps } from '../types'

export function Step1Welcome({ onNext }: StepProps) {
  const { t } = useTranslation()
  const { tenant } = useTenant()
  const isItaly = useIsItaly()
  const {
    patientNumber,
    patientNumberLocked,
    setPatientNumber,
    setPatientNumberLocked
  } = useOnboardingStore()

  const [error, setError] = useState<string | undefined>()

  // Dynamic patient number format based on org config (default: 5 digits → P12345)
  const digits = tenant?.patientNumberDigits ?? 5
  const patientNumberRegex = new RegExp(`^P\\d{${digits}}$`)
  const maxLength = digits + 1 // "P" + digits

  // Check for URL parameter on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const pidFromUrl = urlParams.get('pid')

    if (pidFromUrl) {
      const pid = pidFromUrl.toUpperCase()
      const store = useOnboardingStore.getState()
      // Shared device (family, practice tablet): a link for a different patient
      // must never inherit the previous patient's persisted answers.
      if (store.patientNumber && store.patientNumber !== pid) store.reset()
      setPatientNumber(pid)
      setPatientNumberLocked(true)
    }
  }, [setPatientNumber, setPatientNumberLocked])

  const isValidFormat = patientNumberRegex.test(patientNumber)
  const isValid = isValidFormat

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase()
    setPatientNumber(value)

    // Clear error while typing, validate on blur
    if (error) {
      setError(undefined)
    }
  }

  const handleBlur = () => {
    if (patientNumber && !isValidFormat) {
      setError(t('step1.validation'))
    } else {
      setError(undefined)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isValid) {
      // Log to backend server console
      submissionService.logStart(patientNumber)
      // Mark flow as started — gives admins visibility into stuck patients
      logFlowEvent(patientNumber, 'step1_welcome', 'flow_started')
      onNext()
    }
  }

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="form-inner py-12">
        <div className="flex justify-center mb-8">
          <Logo size={150} />
        </div>

        <h1 className="text-h1 text-foreground mb-4 text-center">
          {t('step1.title', { companyName: tenant?.companyName || 'GAM Medical' })}
        </h1>

        <p className="text-body text-muted-foreground mb-6 max-w-md mx-auto text-center">
          {t('step1.subtitle')}
        </p>

        {/* Expectation setting: what to have ready and how long it takes.
            Welcome is the single largest abandonment point. */}
        <div className="max-w-md mx-auto mb-8 rounded-lg border border-border bg-muted/40 p-4 text-small text-foreground">
          {/* Tenant-aware: the patient number is usually prefilled by the
              magic link; the SMS code only exists for orgs with contracts;
              insurance is never required — it is recorded, not billed.
              The list is omitted entirely when nothing applies. */}
          {(!patientNumberLocked || (tenant?.contracts?.length ?? 0) > 0) && (
            <>
              <p className="font-medium mb-2">{t('step1.checklistTitle')}</p>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground mb-3">
                {!patientNumberLocked && <li>{t('step1.checklistId')}</li>}
                {(tenant?.contracts?.length ?? 0) > 0 && <li>{t('step1.checklistPhone')}</li>}
              </ul>
            </>
          )}
          <p className="text-muted-foreground">{t('step1.duration')}</p>
        </div>

        {/* Italy: the patient number is an internal identifier the patient is
            not expected to know — when the system prefilled it (magic link /
            ?pid), keep it internal and show no field at all. Without a prefill
            the field stays as a fallback so the flow is never a dead end. */}
        <div className={isItaly && patientNumberLocked ? 'hidden' : 'max-w-sm mx-auto mb-8'}>
          <Input
            id="patientNumber"
            type="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            label={t('step1.patientNumber')}
            placeholder={t('step1.placeholder')}
            value={patientNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={patientNumberLocked}
            icon={patientNumberLocked ? <Lock className="w-5 h-5" /> : undefined}
            className={patientNumberLocked ? 'bg-gray-100 cursor-not-allowed' : ''}
            error={error}
            maxLength={maxLength}
          />
          {patientNumberLocked && (
            <p className="text-small text-muted-foreground mt-2">
              {t('step1.locked')}
            </p>
          )}
        </div>

        {/* Spacer for fixed button area on mobile */}
        <div className="h-20 md:hidden" />

        {/* Fixed on mobile, normal flow on desktop */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:relative md:bg-transparent md:border-0 md:p-0">
          <div className="flex justify-center max-w-md mx-auto md:max-w-none">
            <Button type="submit" disabled={!isValid} className="min-w-[200px]">
              {t('common.next')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
